import clsx from 'clsx';
import React from 'react';
import { Button, IconButton } from './Button';
import { IconChevronLeft, IconChevronRight } from './icons';

/**
 * PeriodNav: ‹ label › with a quiet way back to the current period. For
 * billing periods, months, weeks — anything a page reads one window at a
 * time. The label is tabular so the width is steady while paging.
 */
export const PeriodNav: React.FC<{
  label: React.ReactNode;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  /** When set and `isCurrent` is false, a ghost "Current period" button appears. */
  onCurrent?: () => void;
  isCurrent?: boolean;
  currentLabel?: string;
  prevLabel?: string;
  nextLabel?: string;
  size?: 'sm' | 'md';
  minWidth?: number;
  className?: string;
}> = ({
  label,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onCurrent,
  isCurrent = true,
  currentLabel = 'Current period',
  prevLabel = 'Previous period',
  nextLabel = 'Next period',
  size = 'sm',
  minWidth = 176,
  className,
}) => (
  <div className={clsx('flex items-center gap-1', className)}>
    <IconButton
      label={prevLabel}
      size={size}
      variant="secondary"
      disabled={!hasPrev}
      onClick={onPrev}
    >
      <IconChevronLeft size={14} />
    </IconButton>
    <span
      className={clsx(
        'v2-num px-1.5 text-center font-medium text-v2-fg',
        size === 'sm' ? 'text-v2-12' : 'text-v2-13',
      )}
      style={{ minWidth }}
      aria-live="polite"
    >
      {label}
    </span>
    <IconButton
      label={nextLabel}
      size={size}
      variant="secondary"
      disabled={!hasNext}
      onClick={onNext}
    >
      <IconChevronRight size={14} />
    </IconButton>
    {onCurrent && !isCurrent && (
      <Button size={size} variant="ghost" onClick={onCurrent} className="ml-1">
        {currentLabel}
      </Button>
    )}
  </div>
);
